import React, { FC, useState, useEffect, startTransition } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { DeviceUpdate, DeviceStatus, DeviceAdminDetail } from 'src/api/Api';
import { AdminAction } from 'src/state-management/admin.action';

const getDeviceStatusChip = (status: DeviceStatus) => {
  let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
  switch (status) {
    case DeviceStatus.ACTIVE:
      color = 'success';
      break;
    case DeviceStatus.INACTIVE:
      color = 'default';
      break;
    case DeviceStatus.DEFECTIVE:
      color = 'error';
      break;
    case DeviceStatus.LOW_BATTERY:
      color = 'warning';
      break;
    case DeviceStatus.RETIRED:
      color = 'secondary';
      break;
    default:
      break;
  }
  return <Chip label={status} color={color} size="small" />;
};

const DeviceCard = ({ device }: { device: DeviceAdminDetail }) => (
  <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #eee' }}>
    <Typography variant="h6" gutterBottom>Device Details</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Device ID:</strong> {device.human_readable_id}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Serial No:</strong> {device.serial_number}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Type:</strong> {device.type}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Purchased At:</strong> {device.purchased_at ? new Date(device.purchased_at).toLocaleDateString() : 'N/A'}</Typography></Grid>
    </Grid>
  </Paper>
);

const EditDevicePage: FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();

  const [formData, setFormData] = useState<DeviceUpdate>({});
  const [deviceToEdit, setDeviceToEdit] = useState<DeviceAdminDetail | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchAndSetDevice = async () => {
      let device: DeviceAdminDetail | undefined;
      if (state.admin.devices.length === 0) {
        try {
          const response = await clientService.api.getAllDevicesAdminApiV1AdminDevicesGet();
          const devices = response.data;
          dispatch({ type: 'ADMIN_SET_DEVICES', payload: devices } as AdminAction);
          device = devices.find((d) => d.device_id === deviceId);
        } catch (error) {
          showSnackbar({ message: 'Failed to fetch devices', type: snackbarType.ERROR });
          setLoading(false);
          return;
        }
      } else {
        device = state.admin.devices.find((d) => d.device_id === deviceId);
      }

      if (device) {
        setDeviceToEdit(device);
        setFormData({
          name: device.name,
          status: device.status,
        });
      } else {
        showSnackbar({ message: 'Device not found', type: snackbarType.ERROR });
        navigate('/admin/devices');
      }
      setLoading(false);
    };

    if (deviceId) {
      fetchAndSetDevice();
    }
  }, [deviceId, state.admin.devices, dispatch, navigate, showSnackbar, snackbarType.ERROR]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      showSnackbar({ message: 'Please fill in all required fields.', type: snackbarType.ERROR });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!deviceId) return;
      const response = await clientService.api.updateApiDeviceApiV1AdminDevicesDeviceIdPut(deviceId, formData);
      showSnackbar({ message: 'Device updated successfully!', type: snackbarType.SUCCESS });

      const updatedDevices = state.admin.devices.map((device) =>
        device.device_id === deviceId ? response.data : device
      );
      dispatch({ type: 'ADMIN_SET_DEVICES', payload: updatedDevices } as AdminAction);

      startTransition(() => {
        navigate('/admin/devices');
      });
    } catch (error: any) {
      const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to update device.';
      showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    startTransition(() => {
      navigate(-1);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleCancel} color="primary" sx={{ mr: 1 }} aria-label="Back to devices" disabled={isSubmitting}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">Edit Device</Typography>
        </Box>

        {deviceToEdit && <DeviceCard device={deviceToEdit} />}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Device Name"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required disabled={isSubmitting}>
              <InputLabel id="device-status-label">Status</InputLabel>
              <Select
                labelId="device-status-label"
                id="device-status-select"
                name="status"
                value={formData.status || ''}
                label="Status"
                onChange={handleChange}
              >
                {Object.values(DeviceStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {getDeviceStatusChip(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
          <Button onClick={handleCancel} color="inherit" disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditDevicePage;
